import React, { useState, useEffect } from 'react';
import { complaintService } from '../../services/complaint/complaintService';
import { Complaint } from '../../types/complaint';
import { ComplaintTable } from '../../components/complaint/ComplaintTable';
import { RaiseComplaintModal } from '../../components/complaint/RaiseComplaintModal';
import { useNavigation } from '../../context/NavigationContext';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Plus, Search, Filter, LayoutGrid, List } from 'lucide-react';

export const ComplaintListPage: React.FC<{ onSelectComplaint: (c: Complaint) => void }> = ({ onSelectComplaint }) => {
  const { pendingAction, clearAction } = useNavigation();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchComplaints = async () => {
    setIsLoading(true);
    const res = await complaintService.getComplaints({
      search,
      status: statusFilter,
      priority: priorityFilter,
    });
    if (res.success) {
      setComplaints(res.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchComplaints();
  }, [search, statusFilter, priorityFilter]);

  useEffect(() => {
    if (pendingAction === 'raise-complaint') {
      setIsModalOpen(true);
      clearAction();
    }
  }, [pendingAction, clearAction]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Complaint Register</h2>
          <p className="text-xs text-slate-400">View, search, filter and raise facility maintenance complaints</p>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" />} onClick={() => setIsModalOpen(true)}>
          Raise New Complaint
        </Button>
      </div>

      {/* Filter Controls Bar */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search CMP ID, Title..."
            className="w-full bg-slate-950 border border-slate-700 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-200"
          >
            <option value="ALL">All Statuses</option>
            <option value="NEW">New</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="VERIFIED">Verified</option>
          </select>

          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={e => setPriorityFilter(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-200"
          >
            <option value="ALL">All Priorities</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          {/* View Mode Toggle */}
          <div className="flex items-center border border-slate-800 rounded-lg p-0.5 bg-slate-950">
            <button
              onClick={() => setViewMode('table')}
              className={`p-1 rounded ${viewMode === 'table' ? 'bg-slate-800 text-sky-400' : 'text-slate-500'}`}
              title="Table View"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-1 rounded ${viewMode === 'cards' ? 'bg-slate-800 text-sky-400' : 'text-slate-500'}`}
              title="Card Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Main List Table / Cards */}
      {isLoading ? (
        <LoadingSpinner size="lg" text="Loading Complaint Registry..." />
      ) : (
        <ComplaintTable complaints={complaints} onSelect={onSelectComplaint} viewMode={viewMode} />
      )}

      {/* Raise Modal */}
      <RaiseComplaintModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchComplaints}
      />
    </div>
  );
};
